import React from 'react';
import {Typography, Stack, IconButton, Tooltip, CircularProgress, ToggleButton, ToggleButtonGroup} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EditableCell from './EditableCell';
import EditableHeader from '../EditableHeader';
import {useTranslation} from 'react-i18next';
import {alpha} from '@mui/material/styles';
import {Checkbox} from '@mui/material';
import {Box} from '@mui/material';

export default function ResultTable({
                                        files,
                                        results,
                                        toolDetails,
                                        editedValues,
                                        editingCell,
                                        setEditingCell,
                                        editValue,
                                        setEditValue,
                                        handleConfirm,
                                        handleCancel,
                                        theme,
                                        onFilenameClick,
                                        onCellClick,
                                        onCellDoubleClick,
                                        onCellHover,
                                        hoveredKeyExternal,
                                        selectedExecutionId,
                                        onCellRedraw,
                                        overrides,
                                        extractingCell,
                                        processingCell,
                                        rightExtra,
                                        leftExtra,
                                        onToggleRow,
                                        selectedIds,
                                        deletedIds,
                                        page,
                                        hasNext,
                                        hasPrev,
                                        onNext,
                                        onPrev,
                                        total,
                                        pageSize,
                                        onPageSizeChange,
                                        onHeaderEdit
                                    }) {

    const {t} = useTranslation();
    // ── Sticky header support (match HistoryTable)
    const theadRef = React.useRef(null);
    const [editingHeader, setEditingHeader] = React.useState(null); // {row, col}

    const PaginationBar = ({
                               page,
                               hasNext,
                               hasPrev,
                               onNext,
                               onPrev,
                               total,
                               pageSize,
                               onPageSizeChange,
                               leftExtra,
                               rightExtra
                           }) => {
        const hasTotals = Number.isFinite(total) && Number.isFinite(pageSize) && pageSize > 0;
        const totalPages = hasTotals ? Math.max(1, Math.ceil(total / pageSize)) : null;
        const label = hasTotals ? `${page} / ${totalPages}` : `${page}`;
        return (
            <Stack direction="row" alignItems="center" sx={{my: 1, width: '100%'}} justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 200}}>
                    {leftExtra ?? null}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Tooltip title={t('pagination.previousPage')}>
                        <span><IconButton size="small" onClick={onPrev} disabled={!hasPrev}><ChevronLeftIcon
                            fontSize="small"/></IconButton></span>
                    </Tooltip>
                    <Typography variant="body2" sx={{minWidth: 40, textAlign: 'center'}}>{label}</Typography>
                    <Tooltip title={t('pagination.nextPage')}>
                        <span><IconButton size="small" onClick={onNext} disabled={!hasNext}><ChevronRightIcon
                            fontSize="small"/></IconButton></span>
                    </Tooltip>
                    {onPageSizeChange && (
                        <Stack direction="row" alignItems="center" sx={{ml: 2}}>
                            <ToggleButtonGroup value={pageSize} exclusive size="small"
                                               onChange={(e, v) => v && onPageSizeChange(v)}>
                                {[10, 20, 30, 50].map((s) => (<ToggleButton key={s} value={s}>{s}</ToggleButton>))}
                            </ToggleButtonGroup>
                        </Stack>
                    )}
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" sx={{minWidth: 120, justifyContent: 'flex-end'}}>
                    <Typography variant="caption" color="text.secondary" sx={{textAlign: 'right'}}>
                        {Number.isFinite(total) ? `${t('pagination.total')}: ${total} ${t('pagination.rows')}` : ''}
                    </Typography>
                    {rightExtra ?? null}
                </Stack>
            </Stack>
        );
    };

    // Delay single-click "jiggle" so we can cancel it if a double-click happens
    const clickTimerRef = React.useRef(null);

    const computeSpans = (headers) => {
        const numRows = headers.length;
        const numCols = headers[0].length;
        const skip = Array.from({length: numRows}, () => Array(numCols).fill(false));
        const result = [];

        for (let row = 0; row < numRows; row++) {
            const rowCells = [];
            for (let col = 0; col < numCols; col++) {
                if (skip[row][col]) continue;
                const value = headers[row][col];
                if (!value) continue;
                let rowSpan = 1;
                let colSpan = 1;

                for (let r = row + 1; r < numRows; r++) {
                    if (!headers[r][col]) {
                        rowSpan++;
                        skip[r][col] = true;
                    } else break;
                }
                for (let c = col + 1; c < numCols; c++) {
                    const v2 = headers[row][c];

                    // allow merging if blank OR identical
                    const canMergeByValue = (v2 === '' || v2 === value);
                    if (!canMergeByValue) break;

                    // require same parents (rows above must match)
                    let sameParents = true;
                    for (let pr = 0; pr < row; pr++) {
                        if (headers[pr][col] !== headers[pr][c]) {
                            sameParents = false;
                            break;
                        }
                    }
                    if (!sameParents) break;

                    colSpan++;
                    skip[row][c] = true;
                }
                rowCells.push({value, rowSpan, colSpan});
            }
            result.push(rowCells);
        }
        return result;
    };

    // Compute top offsets for each header row so multi-row headers stack correctly (like HistoryTable)
    const [topOffsets, setTopOffsets] = React.useState([]);
    React.useLayoutEffect(() => {
        if (theadRef.current) {
            const rows = Array.from(theadRef.current.children || []);
            const offsets = [];
            let acc = 0;
            for (const row of rows) {
                offsets.push(acc);
                acc += row.offsetHeight;
            }
            if (JSON.stringify(offsets) !== JSON.stringify(topOffsets)) setTopOffsets(offsets);
        }
    }, [topOffsets]);

    // --- helpers to normalize and key-match
    const norm = (s) =>
        (s ?? "")
            .toString()
            .trim()
            .toLowerCase()
            .replace(/[：:]+$/g, ""); // drop trailing colons (full-width & ASCII)


// Extract a "key" from an annotation (works for both your shapes)
    const keyFromAnnotation = (a) => {
        if (!a || !a.type) return null;
        if (a.type === "key_value") {
            const ft = a.field_text;
            if (typeof ft === "string" && ft.trim()) return norm(ft);
            const f = a.field;
            if (typeof f === "string" && f.trim()) return norm(f);
            if (f && typeof f === "object") {
                const t = f.text || f.label || "";
                if (t && t.trim()) return norm(t);
            }
            return null;
        }
        if (a.type === "entity" || a.type === "summary_row") {
            const et = a.entity_type || a.label;
            if (typeof et === "string" && et.trim()) return norm(et);
            return null;
        }
        return null;
    };

    // Get the leaf label for each data column (skip the prepended "Filename" col)
    const getLeafLabels = (headers) => {
        if (!headers?.length) return [];
        const rows = headers.length;
        const cols = headers[0].length;
        const labels = [];
        for (let c = 1; c < cols; c++) { // start at 1 to skip "Filename"
            let label = "";
            // bottom-up: last non-empty in this column
            for (let r = rows - 1; r >= 0; r--) {
                const v = headers[r][c];
                if (v && String(v).trim()) {
                    label = String(v);
                    break;
                }
            }
            // fallback: top-down first non-empty
            if (!label) {
                for (let r = 0; r < rows; r++) {
                    const v = headers[r][c];
                    if (v && String(v).trim()) {
                        label = String(v);
                        break;
                    }
                }
            }
            labels.push(label);
        }
        return labels;
    };


    if (!toolDetails || !toolDetails.headerConfig || results.length === 0) return null;

    const headers = JSON.parse(JSON.stringify(toolDetails.headerConfig));
    if (!headers || headers.length === 0) {
        return <Typography>No header config available.</Typography>;
    }

    headers.forEach((row, idx) => {
        if (idx === 0) row.unshift(t('historyTable.filename'));
        else row.unshift("");
    });

    const SelectAllHeader = ({rows, selectedIds, onToggleRow}) => {
        const all = rows?.length > 0 && rows.every(r => selectedIds?.has?.(r?.data?.execution_id));
        return (
            <Checkbox
                size="small"
                checked={!!all}
                indeterminate={!all && (selectedIds?.size > 0)}
                onChange={(e) => {
                    const on = e.target.checked;
                    rows?.forEach(rr => onToggleRow?.(rr?.data?.execution_id, on));
                }}
                inputProps={{'aria-label': 'Select all'}}
            />
        );
    };

    const headersWithSelect = headers.map((row, idx) => {
        const r = [...row];
        if (idx === 0) r.unshift(<SelectAllHeader rows={results} selectedIds={selectedIds} onToggleRow={onToggleRow}/>);
        else r.unshift('');
        return r;
    });

    const headerSpans = computeSpans(headersWithSelect);
    const numDataColumns = headers[0].length - 1;

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
            <PaginationBar
                page={page}
                hasNext={hasNext}
                hasPrev={hasPrev}
                onNext={onNext}
                onPrev={onPrev}
                total={total}
                pageSize={pageSize}
                onPageSizeChange={onPageSizeChange}
                leftExtra={leftExtra}
                rightExtra={rightExtra}
            />
            <div
                style={{
                    border: "1px solid #ccc",
                    // Match HistoryTable container corners beneath sticky header
                    borderRadius: "0 0 12px 12px",
                    // Horizontal scrolling only; sticky header stays visible
                    overflowX: "auto",
                }}
            >
                <table style={{
                    borderCollapse: "separate",
                    borderSpacing: 0,
                    width: "max-content",
                    minWidth: "100%",
                }}>
                    {/* colgroup to match History: a narrow sticky checkbox col + the rest */}
                    <colgroup>
                        {/* checkbox column */}
                        <col style={{width: 36, minWidth: 36, maxWidth: 40}}/>
                        {/* the rest auto based on headers */}
                        {Array.from({length: (headersWithSelect?.[0]?.length ?? 1) - 1}).map((_, i) => (
                            <col key={i}/>
                        ))}
                    </colgroup>
                    <thead ref={theadRef}>
                    {headerSpans.map((row, rowIndex) => {
                        // Track the actual column index accounting for colSpans AND rowSpans
                        let actualColumnIndex = 0;
                        const occupiedColumns = new Set();

                        // Check all previous rows to see which columns have rowSpans extending into this row
                        for (let prevRow = 0; prevRow < rowIndex; prevRow++) {
                            let colIdx = 0;
                            headerSpans[prevRow].forEach(prevCell => {
                                const cellRowSpan = prevCell.rowSpan || 1;
                                const cellColSpan = prevCell.colSpan || 1;

                                // If this cell's rowSpan extends into the current row
                                if (prevRow + cellRowSpan > rowIndex) {
                                    // Mark all columns it occupies
                                    for (let c = colIdx; c < colIdx + cellColSpan; c++) {
                                        occupiedColumns.add(c);
                                    }
                                }
                                colIdx += cellColSpan;
                            });
                        }

                        return (
                            <tr key={`header-row-${rowIndex}`}>
                                {row.map((cell, renderedCellIndex) => {
                                    // Skip occupied columns to find the actual column index
                                    while (occupiedColumns.has(actualColumnIndex)) {
                                        actualColumnIndex++;
                                    }

                                    // Save the current column index before incrementing
                                    const currentColumnIndex = actualColumnIndex;

                                    // Increment by colSpan for next cell
                                    actualColumnIndex += (cell.colSpan || 1);

                                    // Skip the checkbox and first 2 fixed columns (checkbox + filename)
                                    const isFixedColumn = currentColumnIndex <= 1;
                                    // Allow editing for all non-fixed columns (including empty cells)
                                    const isEditable = onHeaderEdit && !isFixedColumn;

                                    return (
                                        <th
                                            key={`header-cell-${rowIndex}-${renderedCellIndex}`}
                                            colSpan={cell.colSpan}
                                            rowSpan={cell.rowSpan}
                                            style={{
                                                padding: isEditable ? "4px" : "8px",
                                                textAlign: "center",
                                                // match History header bg/fg
                                                background: theme.palette.mode === 'dark' ? '#2c2c2c' : '#f0f0f0',
                                                color: theme.palette.mode === 'dark' ? theme.palette.text.primary : '#1a1a1a',
                                                border: '1px solid #ccc',
                                                // Make all header cells sticky vertically, and the first column sticky horizontally,
                                                // exactly like HistoryTable
                                                position: 'sticky',
                                                top: topOffsets[rowIndex] ?? 0,
                                                left: renderedCellIndex === 0 ? 0 : undefined,
                                                // Checkbox column gets highest z-index (sticky top+left), then headers (sticky top), then normal cells
                                                zIndex: renderedCellIndex === 0 ? 103 : 102,
                                            }}
                                        >
                                            {isEditable ? (
                                                <EditableHeader
                                                    value={cell.value}
                                                    isEditing={editingHeader?.row === rowIndex && editingHeader?.col === currentColumnIndex}
                                                    onStartEdit={() => setEditingHeader({row: rowIndex, col: currentColumnIndex})}
                                                    onSave={(newValue) => {
                                                        onHeaderEdit(rowIndex, currentColumnIndex, newValue, cell.colSpan || 1);
                                                        setEditingHeader(null);
                                                    }}
                                                    onCancel={() => setEditingHeader(null)}
                                                />
                                            ) : (
                                                cell.value
                                            )}
                                        </th>
                                    );
                                })}
                            </tr>
                        );
                    })}
                    </thead>
                    <tbody>
                    {files.map(({file}, idx) => {
                        const res = results[idx]; // align by index to avoid duplicate name ambiguity
                        const executionId = res?.data?.execution_id; // from backend
                        if (deletedIds?.has?.(executionId)) return null;
                        let rowValues = new Array(numDataColumns).fill("");

                        // Will hold { page, bbox } for each data column
                        const metaByCol = new Array(numDataColumns).fill(null);

                        if (res && res.status === "success" && res.data.annotations) {
                            const annots = res.data.annotations;

                            const firstMatch = new Map();  // key -> text
                            const firstMeta = new Map();  // key -> {page,bbox}

                            for (const a of annots) {
                                // NEW: expand key_value_group into per-item virtual key_values
                                if (a.type === "key_value_group" && Array.isArray(a.items)) {
                                    const page = a.page ?? a.source?.page ?? 1;
                                    for (const item of a.items) {
                                        const k = norm(
                                            (item?.field?.text) ??
                                            (typeof item?.field === "string" ? item.field : "")
                                        );
                                        if (!k || firstMatch.has(k)) continue;
                                        const text = item?.value?.text ?? "";
                                        firstMatch.set(k, text);
                                        const bbox = item?.value?.bbox || null;
                                        if (bbox) firstMeta.set(k, {page, bbox});
                                    }
                                    continue; // handled this annotation
                                }
                                const k = keyFromAnnotation(a);
                                if (k && !firstMatch.has(k)) {
                                    if (a.type === "key_value") firstMatch.set(k, a.value?.text || "");
                                    else if (a.type === "entity" || a.type === "summary_row") firstMatch.set(k, a.text || "");
                                    // Save where to jiggle: prefer the "value" box for key_value, else a.bbox
                                    let page = a.page ?? null;
                                    let bbox = null;
                                    if (a.type === "key_value") {
                                        bbox = a.value?.bbox || a.field?.bbox || null;
                                        // some payloads carry page on field/value; fall back safely
                                        page = (a.value?.page ?? a.field?.page ?? a.page ?? 1);
                                    } else if (a.bbox) {
                                        bbox = a.bbox;
                                        page = a.page ?? 1;
                                    }
                                    if (bbox && Array.isArray(bbox) && bbox.length === 4) {
                                        firstMeta.set(k, {page, bbox});
                                    }
                                }
                            }

                            const leafHeaders = getLeafLabels(headers);
                            const values = leafHeaders.map((label, colIdx) => {
                                const target = norm(label);
                                if (!target) return "";
                                if (firstMatch.has(target)) return firstMatch.get(target);
                                for (const [k, v] of firstMatch.entries()) {
                                    if (k.includes(target)) return v;
                                }
                                return "";
                            });

                            // Build meta array in the same order as the values
                            leafHeaders.forEach((label, colIdx) => {
                                const target = norm(label);
                                if (!target) return;
                                if (firstMeta.has(target)) {
                                    metaByCol[colIdx] = firstMeta.get(target);
                                    return;
                                }
                                for (const [k, m] of firstMeta.entries()) {
                                    if (k.includes(target)) {
                                        metaByCol[colIdx] = m;
                                        break;
                                    }
                                }
                            });

                            while (values.length < numDataColumns) values.push("");
                            if (values.length > numDataColumns) values.length = numDataColumns;
                            rowValues = values;
                        }

                        const localEdits = editedValues[executionId ?? file.name] ?? rowValues;

                        // selection by DB id
                        const isSelected = selectedExecutionId && executionId === selectedExecutionId;
                        const isBulkSelected = selectedIds?.has?.(executionId);

                        // subtle, border-safe gradient
                        const left = alpha(theme.palette.primary.light, 0.15);
                        const right = alpha(theme.palette.primary.main, 0.08);
                        const selectedBg = `linear-gradient(90deg, ${left}, ${right})`;
                        const selectedTdStyle = (isSelected || isBulkSelected) ? {
                            backgroundImage: selectedBg,
                            backgroundSize: "100% 100%",
                            backgroundRepeat: "no-repeat",
                            backgroundClip: "padding-box",
                            backgroundOrigin: "padding-box",
                        } : {};

                        return (
                            <tr
                                key={
                                    // Prefer stable per-file itemKey (matches idempotency_key on backend)
                                    (res?.itemKey && `item-${res.itemKey}`) ||
                                    (executionId && `exec-${executionId}`) ||
                                    `row-${idx}`
                                }
                                aria-selected={isSelected ? "true" : "false"}
                                style={{
                                    transition: "background 0.25s ease",
                                }}
                            >

                                {/* selection checkbox cell */}
                                <td
                                    style={{
                                        padding: 0,
                                        border: "1px solid #ccc",
                                        textAlign: "center",
                                        width: 36, minWidth: 36, maxWidth: 40,
                                        position: 'sticky', left: 0, zIndex: 101,
                                        background: theme.palette.background.paper,
                                        ...selectedTdStyle,
                                    }}
                                >
                                    <Checkbox
                                        size="small"
                                        checked={!!isBulkSelected}
                                        onClick={(e) => e.stopPropagation()}
                                        onChange={(e) => onToggleRow?.(executionId, e.target.checked)}
                                        inputProps={{'aria-label': `Select ${file.name}`}}
                                    />
                                </td>

                                <td
                                    style={{
                                        padding: "6px",
                                        border: "1px solid #ccc",
                                        cursor: "pointer",
                                        color: theme.palette.primary.main,
                                        textDecoration: "underline",
                                        ...selectedTdStyle,
                                    }}
                                    onClick={() => {
                                        if (res?.status === "success" && res.data.annotations) {
                                            onFilenameClick(executionId, res.data.annotations, file);
                                        }
                                    }}
                                >
                                    {file.name}
                                </td>

                                {rowValues.map((val, i) => {
                                    const rowIsLoading = res?.status !== "success";
                                    const isEditing =
                                        editingCell &&
                                        (editingCell.executionId
                                            ? editingCell.executionId === executionId
                                            : editingCell.fileName === file.name) &&
                                        editingCell.index === i;

                                    const isExtracting =
                                        extractingCell &&
                                        extractingCell.executionId === executionId &&
                                        extractingCell.colIndex === i;

                                    // Check if this cell is hovered from the PDF
                                    const override = overrides?.[executionId]?.[i] || null;
                                    const meta = override || metaByCol[i];
                                    const cellKey = meta?.page && meta?.bbox ? `${meta.page}_entity_${meta.bbox.join('_')}` : null;
                                    const isCellHovered = cellKey && hoveredKeyExternal === cellKey;

                                    return (
                                        <td
                                            key={`cell-${idx}-${i}`}
                                            style={{
                                                padding: "6px",
                                                border: "1px solid #ccc",
                                                cursor: rowIsLoading ? "default" : "pointer",
                                                position: "relative",
                                                backgroundColor: isCellHovered ? 'rgba(59, 188, 167, 0.3)' : undefined,
                                                transition: 'background-color 0.2s ease',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                maxWidth: '350px',
                                                ...selectedTdStyle,
                                            }}
                                            onClick={() => {
                                                if (rowIsLoading || isEditing) return;
                                                // schedule jiggle; cancel if a double-click occurs
                                                window.clearTimeout(clickTimerRef.current);
                                                clickTimerRef.current = window.setTimeout(() => {
                                                    // prefer an override (newly redrawn box) if available
                                                    const override = overrides?.[executionId]?.[i] || null;
                                                    const meta = override || metaByCol[i];
                                                    if (meta && meta.page && meta.bbox && onCellClick) {
                                                        onCellClick({
                                                            executionId,
                                                            page: meta.page,
                                                            bbox: meta.bbox,
                                                            fileObj: file
                                                        });
                                                    }
                                                }, 250); // 200–300ms is typical; 250ms feels snappy
                                            }}
                                            onMouseEnter={() => {
                                                if (rowIsLoading || isEditing) return;
                                                const override = overrides?.[executionId]?.[i] || null;
                                                const meta = override || metaByCol[i];
                                                if (meta && meta.page && meta.bbox && onCellHover) {
                                                    onCellHover({
                                                        executionId,
                                                        page: meta.page,
                                                        bbox: meta.bbox,
                                                        fileObj: file
                                                    });
                                                }
                                            }}
                                            onMouseLeave={() => {
                                                if (onCellHover) {
                                                    onCellHover(null);
                                                }
                                            }}
                                            onDoubleClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                // cancel pending single-click jiggle
                                                window.clearTimeout(clickTimerRef.current);
                                                if (rowIsLoading || isEditing) return;
                                                if (onCellDoubleClick) {
                                                    onCellDoubleClick({
                                                        executionId,
                                                        fileName: file.name,
                                                        index: i,
                                                        value: (editedValues[executionId ?? file.name]?.[i] ?? val) ?? "",
                                                    });
                                                } else {
                                                    // fallback inline edit
                                                    setEditingCell({executionId, fileName: file.name, index: i});
                                                    setEditValue(
                                                        (editedValues[executionId ?? file.name]?.[i] ?? val) ?? ""
                                                    );
                                                }
                                            }}
                                        >
                                            {/* Redraw pill (visible on hover/focus; clickable) */}
                                            {!rowIsLoading && !isEditing && (
                                                <Tooltip title={t('historyTable.redraw')} arrow>
                                                    <div
                                                        className="cell-actions"
                                                        role="button"
                                                        tabIndex={0}
                                                        aria-label={t('historyTable.redraw')}
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // don't trigger the cell's single-click jiggle
                                                            const meta = overrides?.[executionId]?.[i] || metaByCol[i] || null;
                                                            onCellRedraw?.({
                                                                executionId,
                                                                page: meta?.page,
                                                                bbox: meta?.bbox,
                                                                fileObj: file,
                                                                colIndex: i
                                                            });
                                                        }}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                const meta = overrides?.[executionId]?.[i] || metaByCol[i] || null;
                                                                onCellRedraw?.({
                                                                    executionId,
                                                                    page: meta?.page,
                                                                    bbox: meta?.bbox,
                                                                    fileObj: file,
                                                                    colIndex: i
                                                                });
                                                            }
                                                        }}
                                                        style={{
                                                            position: 'absolute',
                                                            top: 6,
                                                            right: 6,
                                                            fontSize: 11,
                                                            padding: '4px 8px',
                                                            borderRadius: 12,
                                                            background: (theme?.palette?.mode === 'dark') ? '#1f2937' : '#ffffff', // solid bg
                                                            color: (theme?.palette?.mode === 'dark') ? '#e5e7eb' : '#111111',
                                                            border: (theme?.palette?.mode === 'dark')
                                                                ? '1px solid rgba(255,255,255,0.22)'
                                                                : '1px solid rgba(0,0,0,0.22)',
                                                            boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                                                            fontWeight: 600,
                                                            zIndex: 20,
                                                            transition: 'opacity 120ms ease-in-out',
                                                            opacity: 0,
                                                            pointerEvents: 'none',
                                                            cursor: 'pointer',
                                                            // optional: tighten up since it's icon-only
                                                            lineHeight: 1,
                                                        }}
                                                    >
                                                        ↺
                                                    </div>
                                                </Tooltip>


                                            )}
                                            {/* Show the pill on hover/focus with no transparency */}
                                            <style>{`
      td:hover .cell-actions,
      td:focus-within .cell-actions {
        opacity: 1 !important;
        pointer-events: auto !important;
      }
      /* Optional: always show on touch devices (no hover) */
      @media (hover: none) {
        .cell-actions {
          opacity: 1 !important;
          pointer-events: auto !important;
        }
      }
    `}</style>
                                            <EditableCell

                                                value={isEditing ? editValue : (editedValues[executionId ?? file.name]?.[i] ?? val)}
                                                isEditing={isEditing}
                                                onEditStart={() => {
                                                    // kept for completeness; dbl-click is primary
                                                    setEditingCell({executionId, fileName: file.name, index: i});
                                                    setEditValue((editedValues[executionId ?? file.name]?.[i] ?? val) ?? "");
                                                }}
                                                onEditChange={(e) => setEditValue(e.target.value)}
                                                onConfirm={handleConfirm}
                                                onCancel={handleCancel}
                                                isLoading={rowIsLoading || isExtracting || (processingCell?.executionId === executionId && processingCell?.colIndex === i)}
                                            />
                                        </td>
                                    );
                                })}

                            </tr>
                        )
                            ;
                    })}
                    </tbody>


                </table>
            </div>
            {/*<PaginationBar*/}
            {/*    page={page}*/}
            {/*    hasNext={hasNext}*/}
            {/*    hasPrev={hasPrev}*/}
            {/*    onNext={onNext}*/}
            {/*    onPrev={onPrev}*/}
            {/*    total={total}*/}
            {/*    pageSize={pageSize}*/}
            {/*    onPageSizeChange={onPageSizeChange}*/}
            {/*    leftExtra={leftExtra}*/}
            {/*    rightExtra={rightExtra}*/}
            {/*/>*/}
        </Box>

    );
}
